// /account — auto-gestion del admin actual.
//
// Permite cambiar display_name y password. El email NO es editable —
// limitacion del pool Cognito (UsernameAttributes=email, ver
// docs/architecture/auth-flows.md). Cuando el admin necesita cambiar email,
// otro admin lo borra y re-crea.

import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminMe, updateMyProfile, changeMyPassword, AdminApiError } from '@/lib/api';
import { fieldCls } from '@/lib/form-styles';

export default function Account(): JSX.Element {
  return (
    <div className="space-y-6 max-w-2xl">
      <header>
        <h1 data-help-page="account.page" data-help="account.page" className="text-2xl font-semibold">Mi cuenta</h1>
        <p className="text-sm text-ink-muted">Editá tu nombre y password.</p>
      </header>
      <div data-help="account.profile">
        <ProfileForm />
      </div>
      <div data-help="account.password">
        <PasswordForm />
      </div>
    </div>
  );
}

function ProfileForm(): JSX.Element {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['admin-me'], queryFn: adminMe });
  const [displayName, setDisplayName] = useState('');
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (me.data) setDisplayName(me.data.display_name);
  }, [me.data]);

  const m = useMutation({
    mutationFn: () => updateMyProfile(displayName),
    onSuccess: () => {
      setOk(true);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['admin-me'] });
      setTimeout(() => setOk(false), 3000);
    },
    onError: (e) =>
      setErr(e instanceof AdminApiError ? e.message : 'No pudimos guardar el perfil.'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setErr(null);
    setOk(false);
    m.mutate();
  }

  if (me.isLoading || !me.data) {
    return <p className="text-sm text-ink-muted">Cargando perfil…</p>;
  }

  return (
    <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-5 space-y-4">
      <h2 className="text-lg font-medium">Perfil</h2>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Email">
          <input
            type="email"
            disabled
            value={me.data.email ?? ''}
            className={fieldCls()}
          />
          <p className="text-xs text-ink-muted mt-1">
            El email no es editable desde aquí. Si necesitás cambiarlo, contactá a otro admin.
          </p>
        </Field>
        <Field label="Nombre">
          <input
            type="text"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={fieldCls()}
          />
        </Field>
        <Field label="Perfil de acceso">
          <input type="text" disabled value={`${me.data.role_name} (${me.data.role})`} className={fieldCls()} />
        </Field>
        {err ? <ErrorBox>{err}</ErrorBox> : null}
        {ok ? (
          <div role="status" className="rounded-lg border border-status-success/30 bg-status-success/10 p-3 text-sm text-status-success">
            Perfil guardado. <span className="text-ink-muted">El nuevo nombre se ve después de cerrar y abrir sesión.</span>
          </div>
        ) : null}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={m.isPending || displayName === me.data.display_name}
            className={btnPrimary}
          >
            {m.isPending ? 'Guardando…' : 'Guardar perfil'}
          </button>
        </div>
      </form>
    </section>
  );
}

function PasswordForm(): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => changeMyPassword(current, next),
    onSuccess: () => {
      setOk(true);
      setCurrent('');
      setNext('');
      setConfirm('');
      setErr(null);
      setTimeout(() => setOk(false), 3000);
    },
    onError: (e) => {
      if (e instanceof AdminApiError && e.message === 'current_password_incorrect') {
        setErr('La contraseña actual no es correcta.');
      } else {
        setErr(e instanceof AdminApiError ? e.message : 'No pudimos cambiar la pwd.');
      }
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setErr(null);
    setOk(false);
    if (next !== confirm) {
      setErr('La nueva password y la confirmación no coinciden.');
      return;
    }
    if (next.length < 8) {
      setErr('La nueva password debe tener al menos 8 caracteres.');
      return;
    }
    m.mutate();
  }

  return (
    <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-5 space-y-4">
      <h2 className="text-lg font-medium">Cambiar password</h2>
      <form onSubmit={onSubmit} className="space-y-3" autoComplete="off">
        <Field label="Password actual">
          <input
            type="password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={fieldCls()}
            autoComplete="current-password"
          />
        </Field>
        <Field label="Nueva password (mín. 8 chars)">
          <input
            type="password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={fieldCls()}
            autoComplete="new-password"
          />
        </Field>
        <Field label="Confirmar nueva password">
          <input
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={fieldCls()}
            autoComplete="new-password"
          />
        </Field>
        {err ? <ErrorBox>{err}</ErrorBox> : null}
        {ok ? (
          <div role="status" className="rounded-lg border border-status-success/30 bg-status-success/10 p-3 text-sm text-status-success">
            Password cambiada. Otras sesiones tuyas fueron revocadas.
          </div>
        ) : null}
        <div className="flex justify-end">
          <button type="submit" disabled={m.isPending} className={btnPrimary}>
            {m.isPending ? 'Cambiando…' : 'Cambiar password'}
          </button>
        </div>
      </form>
    </section>
  );
}

const btnPrimary =
  'min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-4 disabled:opacity-50 disabled:cursor-not-allowed';

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block text-sm space-y-1">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
      {children}
    </div>
  );
}
